import { CARS, configure, currentRules, currentStakes } from './catalog.js';
import { LOADING_ASSETS } from './loading-assets.js';
import { createOffer, sampleRace, scenarioFor, settlementFor } from './race-core.js';
import { drawStoryIndex, storyIndexFromSearch } from './race-stories.js';
import { createRoundStore } from '../../assets/gt-racing-session.js';
import { RaceScene } from './render/scene.js';
import { RaceAudio } from './audio.js';
import { soundFromSearch } from './engine.js';

const $ = selector => document.querySelector(selector);
const game = $('#game');
const format = value => new Intl.NumberFormat('vi-VN').format(value);
const seed = () => crypto.getRandomValues(new Uint32Array(1))[0];
const walletBalance = () => {
  try { return window.ZalaFunWallet?.getSnapshot?.() ?? null; } catch { return null; }
};
const store = createRoundStore({ wallet: window.ZalaFunWallet, bridge: window.ZalaFunGameRound,
  profile: window.ZalaFunProfile, analytics: window.ZalaFunAnalyticsRound });
const CONFIG_REFRESH_INTERVAL_MS = 60000;
let copy, renderer, offer, audio;
let state = store.snapshot();
let coins = walletBalance();
let contextReady = true, lastFrame = null, lastStage = '', lastRank = '', lastLeader = '', lastSave = 0, callUntil = 0;
let soundUnlocked = false, lastEvent = '';
let configFetchedAt = 0, configRequest = null, appliedRules = JSON.stringify(currentRules());
const reviewStoryIndex = storyIndexFromSearch(location.search);
const reviewSound = soundFromSearch(location.search);
let lastSettlementAttempt = -Infinity, settlementWaiting = false, settling = false, starting = false;
const cardButtons = new Map();
const rankRows = new Map();
// Loading progress: every tracked file reports its byte counts when it can, and
// each file carries the weight of its size on disk, because nginx gzips the car
// exports without a Content-Length and their live download is invisible. A file
// counts in full once its model is decoded, so the decode and scene build sit
// inside the bar rather than after it.
const ASSET_WEIGHT = { download: .85, decode: .15 };
const LOADING_BYTES = LOADING_ASSETS.reduce((sum, asset) => sum + asset.bytes, 0);
const loadingView = {
  fill: null, value: null, meter: null, label: null, assets: new Map(), painted: -1,
};

function paintLoading(percent) {
  // A file only joins the denominator once it reports its length, which can pull
  // the ratio back down; the bar keeps the highest value it has shown instead.
  const value = Math.max(loadingView.painted, Math.max(0, Math.min(100, Math.round(percent))));
  if (value === loadingView.painted) return;
  loadingView.painted = value;
  loadingView.value.textContent = String(value);
  loadingView.fill.style.width = `${value}%`;
  loadingView.meter.setAttribute('aria-valuenow', String(value));
}

function paintLoadingPhase(text) {
  if (loadingView.label.textContent === text) return;
  loadingView.label.textContent = text;
}

function updateLoading() {
  let progress = 0, decoded = 0;
  for (const asset of LOADING_ASSETS) {
    const entry = loadingView.assets.get(asset.id);
    if (entry?.decoded) {
      progress += asset.bytes;
      decoded += 1;
      continue;
    }
    if (entry?.total > 0) progress += asset.bytes * Math.min(1, entry.loaded / entry.total);
  }
  const ratio = ASSET_WEIGHT.download * (progress / LOADING_BYTES) + ASSET_WEIGHT.decode * (decoded / LOADING_ASSETS.length);
  paintLoading(4 + 92 * ratio);
  if (!copy) return;
  const cars = CARS.filter(car => loadingView.assets.get(car.id)?.decoded).length;
  paintLoadingPhase(cars < CARS.length
    ? copy.loadingCars.replace('{done}', String(cars)).replace('{total}', String(CARS.length))
    : copy.loadingScene);
}

function reportAsset(patch) {
  const entry = loadingView.assets.get(patch.id) ?? { loaded: 0, total: 0, decoded: false };
  loadingView.assets.set(patch.id, { ...entry, ...patch });
  updateLoading();
}

function restoreOffer() {
  offer = createOffer(state.round?.seed ?? seed());
}

/** The shipped amounts until the served configuration answers, then one button per offered stake. */
function renderAmounts() {
  const picker = $('#amount-picker');
  picker.replaceChildren(...currentStakes().map(stake => {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.amount = String(stake);
    button.setAttribute('role', 'radio');
    button.textContent = format(stake);
    return button;
  }));
}

function apiBase() {
  const base = window.ZalaFunConfig?.apiBase;
  return typeof base === 'string' && base !== '' ? base.replace(/\/+$/, '') : '/api';
}

/**
 * Applies the rules the server currently publishes: the stake buttons, the bet the
 * wallet is asked to hold, and the credit each car pays all follow from them. A round
 * already on the grid keeps the stake and the chances it was priced under.
 */
function applyGameConfig(config) {
  const applied = configure({
    stakes: config.stakes,
    returnRate: config.returnRate === null ? undefined : config.returnRate,
    chanceBasisPoints: config.chanceBasisPoints === null ? undefined : config.chanceBasisPoints,
  });
  const signature = JSON.stringify(applied);
  if (signature === appliedRules) return false;
  appliedRules = signature;

  renderAmounts();
  reconcileStake();
  if (state.stage === 'selecting') restoreOffer();
  updateSelection(true);
  return true;
}

/**
 * Reads the rules the server currently publishes. A guest page, an offline page
 * or an old response keeps the shipped values, so play never blocks on this.
 */
function refreshGameConfig({ force = false } = {}) {
  const now = Date.now();
  if (!force && (configRequest !== null || now - configFetchedAt < CONFIG_REFRESH_INTERVAL_MS)) return configRequest ?? Promise.resolve(null);
  configRequest = (async () => {
    try {
      const response = await fetch(`${apiBase()}/games/gt-racing/config`, {
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) return null;
      const payload = await response.json();
      const config = payload?.data;
      if (!config || config.gameId !== 'gt-racing') return null;
      applyGameConfig(config);
      return config;
    } catch {
      return null;
    } finally {
      configFetchedAt = Date.now();
      configRequest = null;
    }
  })();
  return configRequest;
}

function notify(message, duration = 2800) {
  const notice = $('#notice'); notice.textContent = message; notice.hidden = false;
  if (duration) setTimeout(() => { if (notice.textContent === message) notice.hidden = true; }, duration);
}

async function unlockSound() {
  if (soundUnlocked) return;
  soundUnlocked = true;
  await audio.unlock();
}

function updatePanelSize() {
  game.style.setProperty('--selection-height', `${$('#selection-panel').getBoundingClientRect().height}px`);
}

function updateBalance() {
  const balance = coins?.balance ?? 0;
  $('#balance').textContent = format(balance);
  $('#balance').parentElement.setAttribute('aria-label', `${copy.balanceLabel} ${format(balance)}`);
}

/**
 * Keeps the remembered stake while it is still offered and affordable. A stake the
 * balance can no longer cover drops to the largest option it can pay, and a stake the
 * served ladder no longer offers drops to the smallest one, because the player never
 * picked any option of that ladder.
 */
function reconcileStake() {
  if (state.stage !== 'selecting') return false;
  const offered = currentStakes();
  const balance = coins?.balance ?? 0;
  const next = !offered.includes(state.stake) ? offered[0]
    : state.stake <= balance ? null
      : offered.filter(stake => stake <= balance).at(-1) ?? offered[0];
  if (next === null || next === state.stake) return false;
  try { state = store.selectStake(next); } catch { return false; }
  return true;
}

function onWalletChange(snapshot) {
  coins = snapshot ?? walletBalance();
  if (store.refreshIdentity()) {
    state = store.snapshot();
    enterStage(state.stage);
  }
  updateBalance();
  if (state.stage === 'selecting') { reconcileStake(); updateSelection(); }
}

function updateSelection(animate = false) {
  updateBalance();
  const balance = coins?.balance ?? 0;
  for (const button of document.querySelectorAll('[data-amount]')) {
    const amount = Number(button.dataset.amount);
    button.classList.toggle('selected', amount === state.stake);
    button.setAttribute('aria-checked', String(amount === state.stake));
    button.disabled = amount > balance;
  }
  for (const car of CARS) {
    const button = cardButtons.get(car.id);
    button.classList.toggle('selected', car.id === state.selectedCarId);
    button.setAttribute('aria-checked', String(car.id === state.selectedCarId));
    const amount = Math.round(state.stake * offer.profiles[car.id].factor);
    const value = button.querySelector('.reward-value');
    value.textContent = format(amount);
    value.dataset.reward = String(amount);
    if (animate) { value.classList.remove('is-changing'); void value.offsetWidth; value.classList.add('is-changing'); }
    button.setAttribute('aria-label', `${copy.cars[car.id].name}, ${copy.reward} ${format(amount)} ${copy.coin}`);
    const profile = offer.profiles[car.id].index;
    button.querySelector('.car-shape').textContent = `${copy.cars[car.id].shape.split(' \u00b7 ')[0]} \u00b7 ${copy[`profile${profile}`]}`;
  }
  const selected = CARS.find(car => car.id === state.selectedCarId);
  $('#model-name').textContent = selected ? `${selected.number}  ${copy.cars[selected.id].name}` : copy.selectHeading;
  $('#model-shape').textContent = selected ? copy.cars[selected.id].shape : copy.selectSubheading;
  renderer.showCar(state.selectedCarId);
  const start = $('#start-button');
  start.disabled = !selected || balance < state.stake || state.walletBusy;
  start.firstElementChild.textContent = selected ? `${copy.start} \u00b7 ${format(state.stake)} ${copy.coin}` : copy.chooseCar;
  updatePanelSize();
}

function createCards() {
  $('#car-picker').setAttribute('aria-label', copy.carGroup);
  $('#rank-board').setAttribute('aria-label', copy.raceOrder);
  for (const car of CARS) {
    const row = document.createElement('button');
    row.type = 'button'; row.className = 'car-row'; row.dataset.car = car.id;
    row.style.setProperty('--car-color', car.id === 'titan' ? car.accent : car.color);
    row.setAttribute('role', 'radio');
    row.innerHTML = '<img alt=""><div class="car-info"><div class="car-name-line"><b class="car-number"></b><span class="car-name"></span></div><div class="car-shape"></div></div><div class="reward-preview"><small></small><strong class="reward-value"></strong></div>';
    row.querySelector('img').src = renderer.thumbnails[car.id];
    row.querySelector('.car-number').textContent = car.number;
    row.querySelector('.car-name').textContent = copy.cars[car.id].name;
    row.querySelector('.reward-preview small').textContent = copy.reward;
    row.addEventListener('click', () => {
      unlockSound();
      const chosen = state.selectedCarId;
      try { state = store.selectCar(car.id); } catch { return; }
      if (state.selectedCarId !== chosen) audio.selectionBlip();
      updateSelection(); store.save();
    });
    $('#car-picker').append(row); cardButtons.set(car.id, row);
    const rank = document.createElement('div');
    rank.className = 'rank-item'; rank.dataset.car = car.id;
    rank.style.setProperty('--car-color', car.id === 'titan' ? car.accent : car.color);
    rank.innerHTML = '<span class="rank-position"></span><b class="rank-number"></b><span class="rank-name"></span>';
    rank.querySelector('.rank-number').textContent = car.number;
    rank.querySelector('.rank-name').textContent = copy.cars[car.id].name.replace(' GT', '');
    rankRows.set(car.id, rank);
  }
}

function makeConfetti() {
  const fragment = document.createDocumentFragment();
  for (let index = 0; index < 36; index++) {
    const strip = document.createElement('i');
    strip.style.setProperty('--x', `${(index * 37) % 101}%`);
    strip.style.setProperty('--duration', `${3.5 + (index % 5) * .31}s`);
    strip.style.setProperty('--delay', `${-(index % 8) * .3}s`);
    strip.style.setProperty('--color', ['#ffd83e', '#ffffff', '#e6434d', '#9dcff0'][index % 4]);
    fragment.append(strip);
  }
  $('#confetti').replaceChildren(fragment);
}

function creditedAmount(result) {
  const settlement = state.settlement;
  if (!settlement) return result.reward;
  if (settlement.status === 'unconfirmed') return 0;
  return settlement.payout;
}

function updateResult() {
  if (!state.round) return;
  const result = settlementFor(state.round);
  const winner = CARS.find(car => car.id === result.winnerId);
  const received = creditedAmount(result);
  $('#result-eyebrow').textContent = `${copy.champion} \u00b7 ${winner.number} ${copy.cars[winner.id].name}`;
  $('#result-title').textContent = result.selectedRank === 1 ? copy.correct : copy.missed;
  $('#result-choice').textContent = `${copy.chosenResult}\uff1a${copy.cars[state.selectedCarId].name} \u00b7 ${copy.rank.replace('{rank}', String(result.selectedRank))}`;
  $('#result-used').textContent = `${format(result.amount)} ${copy.coin}`;
  $('#result-received').textContent = `${format(received)} ${copy.coin}`;
  const net = $('#result-net'); net.textContent = `${received - result.amount > 0 ? '+' : ''}${format(received - result.amount)} ${copy.coin}`;
  net.className = received - result.amount > 0 ? 'positive' : 'negative';
  $('#result-balance').textContent = `${format(coins?.balance ?? 0)} ${copy.coin}`;
  const pending = state.settlement?.status === 'pending';
  $('#result-status').textContent = pending ? copy.settlePending : state.settlement?.status === 'unconfirmed' ? copy.settleUnconfirmed : '';
  $('#settle-button').hidden = !pending;
}

function startErrorMessage(error) {
  if (error?.code === 'INSUFFICIENT_FUNDS') return copy.notEnough;
  if (error?.code === 'PRESENTATION_STORAGE_ERROR') return copy.saveError;
  if (error?.code === 'ROUND_ACTIVE' || error?.code === 'PAGE_ROUND_ACTIVE') return copy.roundActive;
  return copy.startError;
}

function enterStage(stage) {
  updateBalance();
  game.dataset.stage = stage;
  $('#race-hud').hidden = stage !== 'racing';
  $('#your-car').hidden = stage === 'selecting' || stage === 'result' || stage === 'celebrating';
  $('#result-panel').hidden = stage !== 'result';
  $('#champion-banner').hidden = stage !== 'celebrating' && stage !== 'result';
  $('#confetti').hidden = stage !== 'celebrating' && stage !== 'result';
  if (state.round) {
    game.dataset.raceVersion = String(state.round.raceVersion);
    game.dataset.scenario = scenarioFor(state.round).id;
    game.dataset.winner = state.round.winnerId;
    const car = CARS.find(car => car.id === state.selectedCarId);
    $('#your-car').style.setProperty('--car-color', car.id === 'titan' ? car.accent : car.color);
    $('#your-car').textContent = copy.yourCar;
    const number = document.createElement('b'); number.textContent = car.number; $('#your-car').append(number);
    $('#champion-name').textContent = `${CARS.find(car => car.id === state.round.winnerId).number} ${copy.cars[state.round.winnerId].name}`;
  }
  if (stage === 'grid') showCall(copy.grid, 1300);
  if (stage === 'countdown') showCall(copy.countdown, 2700);
  if (stage === 'racing') showCall(copy.go, 900);
  if (stage === 'celebrating') { $('#race-call').classList.remove('visible'); makeConfetti(); }
  if (stage === 'result') { updateResult(); $('#replay-button').focus({ preventScroll: true }); }
  if (stage === 'selecting') {
    updateSelection(); lastRank = ''; lastLeader = ''; lastEvent = ''; $('#race-call').classList.remove('visible');
    // A wallet change can drive this stage while the loading bar is still up, and
    // the screen's sound must not start downloading behind it.
    if ($('#loading').hidden) audio.prepareSelection();
  }
  lastStage = stage; store.save();
}

function showCall(message, duration) {
  $('#race-call').textContent = message;
  $('#race-call').classList.add('visible');
  callUntil = performance.now() + duration;
}

function raceClock(elapsed, duration) {
  const time = Math.max(0, elapsed - 5);
  if (state.round?.raceVersion >= 5 || time < duration - 2) return time;
  if (time < duration - 1) return duration - 2 + (time - duration + 2) * 1.5;
  if (time < duration) return duration - .5 + (time - duration + 1) * .5;
  return time;
}

async function settleFinishedRound() {
  if (settling) return;
  settling = true;
  try {
    state = await store.finish();
    if (settlementWaiting) $('#notice').hidden = true;
    settlementWaiting = false;
  } catch {
    if (!settlementWaiting) { settlementWaiting = true; notify(copy.saveResultError, 0); }
  } finally { settling = false; updateResult(); }
}

function animate(now) {
  requestAnimationFrame(animate);
  if (!contextReady || document.hidden) { lastFrame = null; return; }
  const delta = lastFrame === null ? 0 : Math.min(.25, Math.max(0, (now - lastFrame) / 1000));
  lastFrame = now;
  store.advance(delta);
  const progress = store.progress();
  if (progress.round && progress.elapsed >= progress.totalDuration && !progress.settled && now - lastSettlementAttempt > 1000) {
    lastSettlementAttempt = now;
    void settleFinishedRound();
  }
  if (progress.stage !== lastStage) enterStage(progress.stage);
  const sample = progress.round ? sampleRace(progress.round, raceClock(progress.elapsed, progress.round.duration)) : null;
  const stats = renderer.render({ stage: progress.stage, elapsed: progress.elapsed, sample, round: progress.round, selectedCarId: state.selectedCarId, winnerId: progress.round?.winnerId, time: now / 1000 });
  game.dataset.rendered = 'true'; game.dataset.circuit = stats.track; game.dataset.camera = stats.camera;
  game.dataset.drawCalls = String(stats.calls); game.dataset.triangles = String(stats.triangles);
  if (sample && progress.stage === 'racing') {
    $('#phase-chip').textContent = copy[sample.braking ? 'braking' : sample.accelerating ? 'accelerating' : sample.phase];
    game.dataset.speedKph = String(Math.round(sample.cars.find(car => car.id === state.selectedCarId).speed * 3.6));
    game.dataset.motion = sample.braking ? 'braking' : sample.accelerating ? 'accelerating' : sample.phase;
    game.dataset.tactic = sample.event?.kind || 'racingLine';
    $('#race-progress-fill').style.width = `${stats.progress * 100}%`;
    const orderKey = sample.order.join(',');
    if (orderKey !== lastRank) {
      const board = $('#rank-board');
      sample.order.forEach((id, index) => {
        const row = rankRows.get(id); row.querySelector('.rank-position').textContent = String(index + 1);
        row.classList.toggle('is-yours', id === state.selectedCarId); board.append(row);
      });
      lastRank = orderKey;
    }
    if (sample.event && sample.event.id !== lastEvent) {
      const name = copy.cars[sample.event.carId].name.replace(' GT', '');
      showCall(copy.raceEvents[sample.event.kind].replace('{car}', name), 1900);
      lastEvent = sample.event.id;
    } else if (lastLeader && lastLeader !== sample.leader && progress.elapsed > 8 && progress.elapsed < progress.round.duration + 3 && now > callUntil) {
      showCall(`${copy.cars[sample.leader].name} ${copy.leaderChange}`, 1550);
    }
    lastLeader = sample.leader;
    game.dataset.leader = sample.leader;
  }
  if (now > callUntil) $('#race-call').classList.remove('visible');
  audio.update(progress.stage, progress.elapsed, sample, state.selectedCarId);
  if (now - lastSave > 350 && progress.round && !progress.settled) { store.save(); lastSave = now; }
}

function keyboardRadio(container, selector) {
  container.addEventListener('keydown', event => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const items = [...container.querySelectorAll(selector)].filter(button => !button.disabled);
    const index = items.indexOf(document.activeElement);
    if (index < 0 || !items.length) return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = items[(index + direction + items.length) % items.length]; next.click(); next.focus();
  });
}

async function startRace() {
  if (state.stage !== 'selecting' || !contextReady || starting) return;
  unlockSound();
  starting = true;
  try {
    refreshGameConfig();
    const scenarioIndex = reviewStoryIndex ?? drawStoryIndex(state.recentScenarios, seed);
    state = await store.start({ seed: offer.seed, scenarioIndex });
    settlementWaiting = false;
    $('#notice').hidden = true;
    enterStage(state.stage); lastFrame = null;
  } catch (error) {
    console.warn('Unable to start the GT racing round.', error);
    refreshGameConfig({ force: true });
    notify(startErrorMessage(error));
  } finally { starting = false; updateSelection(); }
}

async function boot() {
  const response = await fetch('locale/vi.json?v=20261008-TgRmQa');
  if (!response.ok) throw new Error('Locale unavailable.');
  copy = await response.json();
  document.title = copy.title;
  document.querySelectorAll('[data-copy]').forEach(node => { node.textContent = copy[node.dataset.copy] || ''; });
  $('#loading-label').textContent = copy.loading;
  loadingView.fill = $('#loadingFill');
  loadingView.value = $('#loadingValue');
  loadingView.meter = $('#loadingMeter');
  loadingView.label = $('#loading-label');
  paintLoading(2);
  restoreOffer();
  coins = walletBalance();
  updateBalance();
  audio = new RaceAudio({ profile: reviewSound || 'apex', onError: () => notify(copy.audioLoadError, 0) });
  game.dataset.soundProfile = reviewSound || 'apex';
  renderer = new RaceScene($('#scene-view'), ready => {
    contextReady = ready; lastFrame = null;
    if (!ready) { store.save(); audio.pause(); notify(copy.contextLost, 0); }
    else { $('#notice').hidden = true; audio.resume(); }
  }, reportAsset);
  await renderer.ready;
  createCards();
  renderAmounts();
  updateSelection();
  $('#amount-picker').addEventListener('click', event => {
    const button = event.target.closest('[data-amount]');
    if (!button || !$('#amount-picker').contains(button)) return;
    unlockSound();
    const stake = state.stake;
    try { state = store.selectStake(Number(button.dataset.amount)); } catch { return; }
    if (state.stake !== stake) audio.selectionBlip();
    updateSelection(true); store.save();
  });
  refreshGameConfig({ force: true });
  $('#start-button').addEventListener('click', startRace);
  $('#replay-button').addEventListener('click', () => {
    try { state = store.replay(); } catch { return; }
    offer = createOffer(seed()); reconcileStake(); enterStage('selecting');
  });
  $('#settle-button').addEventListener('click', async () => {
    state = await store.retrySettlement();
    updateResult();
    updateBalance();
  });
  keyboardRadio($('#car-picker'), '.car-row'); keyboardRadio($('#amount-picker'), 'button');
  new ResizeObserver(updatePanelSize).observe($('#selection-panel'));
  document.addEventListener('visibilitychange', () => { store.save(); lastFrame = null; if (document.hidden) audio.pause(); else audio.resume(); });
  window.addEventListener('pagehide', () => { store.save(); try { window.ZalaFunGameRound?.abandon?.(); } catch {} });
  try { window.ZalaFunWallet?.subscribe?.(onWalletChange); } catch {}
  paintLoading(97);
  paintLoadingPhase(copy.loadingWallet);
  try { await store.recover(); state = store.snapshot(); } catch (error) { console.warn('Unable to recover the GT racing round.', error); }
  reconcileStake();
  paintLoading(100);
  game.setAttribute('aria-busy', 'false');
  $('#loading').hidden = true;
  enterStage(state.stage);
  requestAnimationFrame(animate);
}

boot().catch(error => {
  console.error('GT racing boot failed.', error);
  $('#start-button').disabled = true;
  $('#loading-label').textContent = copy?.loadError || 'GT';
  if (copy) notify(copy.loadError, 0);
});
